import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from './i18n';
import { fetchMembers, submitTask } from './api';
import type { Member, Priority, TaskFormValues } from './types';

// ---------------------------------------------------------------------------
// Telegram WebApp helpers
// ---------------------------------------------------------------------------

const twa = () => window.Telegram?.WebApp;

function applyTheme(): void {
  const tp = twa()?.themeParams;
  if (!tp) return;
  const root = document.documentElement.style;
  if (tp.bg_color) root.setProperty('--tg-theme-bg-color', tp.bg_color);
  if (tp.text_color) root.setProperty('--tg-theme-text-color', tp.text_color);
  if (tp.hint_color) root.setProperty('--tg-theme-hint-color', tp.hint_color);
  if (tp.button_color) root.setProperty('--tg-theme-button-color', tp.button_color);
  if (tp.button_text_color) root.setProperty('--tg-theme-button-text-color', tp.button_text_color);
  if (tp.secondary_bg_color) root.setProperty('--tg-theme-secondary-bg-color', tp.secondary_bg_color);
  if (tp.destructive_text_color) root.setProperty('--tg-theme-destructive-text-color', tp.destructive_text_color);
  if (tp.link_color) root.setProperty('--tg-theme-link-color', tp.link_color);
}

// ---------------------------------------------------------------------------
// URL param helpers
// ---------------------------------------------------------------------------

function getQueryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key) ?? '';
}

/** Very basic format sanity check. Real trust comes from initData validation on the server. */
function isValidNumericId(v: string): boolean {
  return /^-?\d{1,20}$/.test(v.trim());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ScreenState = 'loading' | 'form' | 'submitting' | 'success' | 'error';

const EMPTY_FORM: TaskFormValues = {
  title: '',
  description: '',
  assigneeId: null,
  priority: '',
  dueDate: '',
  subtasks: [''],
};

export default function App() {
  const listId = getQueryParam('list_id');
  const chatId = getQueryParam('chat_id');

  // ----- State -----
  const [screen, setScreen] = useState<ScreenState>('loading');
  const [members, setMembers] = useState<Member[]>([]);
  const [form, setForm] = useState<TaskFormValues>(EMPTY_FORM);
  const [loadError, setLoadError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');

  // Ref to access current form values inside stable callbacks
  const formRef = useRef(form);
  formRef.current = form;

  // ----- Validate launch params -----
  if (!isValidNumericId(listId) || !isValidNumericId(chatId)) {
    return (
      <div className="state-screen">
        <span className="icon">⚠️</span>
        <h2>Invalid launch URL</h2>
        <p>This app must be opened from the bot.</p>
      </div>
    );
  }

  // ----- Telegram SDK init -----
  useEffect(() => {
    const wa = twa();
    if (!wa) return;
    wa.ready();
    wa.expand();
    applyTheme();
    wa.onEvent('themeChanged', applyTheme);

    // BackButton → close the mini app
    wa.BackButton.show();
    wa.BackButton.onClick(() => wa.close());
  }, []);

  // ----- Load members -----
  const loadMembers = useCallback(async () => {
    setScreen('loading');
    setLoadError('');
    try {
      const data = await fetchMembers(listId);
      setMembers(data);
      setScreen('form');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('fetchMembers error', err);
      setLoadError(msg);
      setScreen('error');
    }
  }, [listId]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // ----- Form validity -----
  const isValid = form.title.trim().length > 0;

  // ----- MainButton sync -----
  useEffect(() => {
    const wa = twa();
    if (!wa) return;
    const btn = wa.MainButton;
    btn.setText(i18n.mainButtonCreate);
    btn.show();
    if (screen === 'submitting') {
      btn.disable().showProgress(false);
    } else if (screen === 'form' && isValid) {
      btn.enable().hideProgress();
    } else {
      btn.disable().hideProgress();
    }
  }, [screen, isValid]);

  // ----- Submit handler (stable ref to avoid re-registering click) -----
  const handleSubmit = useCallback(async () => {
    const f = formRef.current;
    if (!f.title.trim()) return;
    setScreen('submitting');
    setSubmitError('');

    const subtasks = f.subtasks.filter(s => s.trim().length > 0);

    try {
      await submitTask({
        list_id: listId,
        chat_id: chatId,
        title: f.title.trim(),
        description: f.description.trim() || undefined,
        assignee_id: f.assigneeId ?? undefined,
        priority: f.priority || undefined,
        due_date: f.dueDate || undefined,
        subtasks,
      });
      twa()?.HapticFeedback.notificationOccurred('success');
      setScreen('success');
      setTimeout(() => twa()?.close(), 1800);
    } catch (err) {
      const msg = err instanceof Error ? err.message : i18n.errorCreateTask;
      console.error('submitTask error', err);
      twa()?.HapticFeedback.notificationOccurred('error');
      setSubmitError(msg);
      setScreen('form');
    }
  }, [listId, chatId]);

  // Register / unregister MainButton click listener
  useEffect(() => {
    const wa = twa();
    if (!wa) return;
    wa.MainButton.onClick(handleSubmit);
    return () => { wa.MainButton.offClick(handleSubmit); };
  }, [handleSubmit]);

  // ----- Subtask helpers -----
  const updateSubtask = (idx: number, val: string) => {
    setForm(f => {
      const s = [...f.subtasks];
      s[idx] = val;
      return { ...f, subtasks: s };
    });
  };

  const addSubtask = () => {
    setForm(f => ({ ...f, subtasks: [...f.subtasks, ''] }));
  };

  const removeSubtask = (idx: number) => {
    setForm(f => {
      const s = f.subtasks.filter((_, i) => i !== idx);
      return { ...f, subtasks: s.length > 0 ? s : [''] };
    });
  };

  // ----- Screens -----
  if (screen === 'loading') {
    return (
      <div className="state-screen">
        <p>{i18n.loadingMembers}</p>
      </div>
    );
  }

  if (screen === 'success') {
    return (
      <div className="state-screen">
        <span className="icon">✅</span>
        <h2>{i18n.successTitle}</h2>
        <p>{i18n.successSubtitle}</p>
      </div>
    );
  }

  if (screen === 'error') {
    return (
      <div className="state-screen">
        <span className="icon">⚠️</span>
        <h2>Something went wrong</h2>
        <p>{loadError || i18n.errorLoadMembers}</p>
        <button className="btn-add-subtask" onClick={() => void loadMembers()}>
          Retry
        </button>
      </div>
    );
  }

  // ----- Main form -----
  return (
    <div className="app">
      <div className="form-body">

        {/* Title */}
        <div className="section">
          <label className="field-label" htmlFor="title">{i18n.labelTitle} *</label>
          <input
            id="title"
            type="text"
            placeholder={i18n.placeholderTitle}
            value={form.title}
            maxLength={500}
            autoFocus
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          />
        </div>

        {/* Assignee */}
        <div className="section">
          <label className="field-label" htmlFor="assignee">{i18n.labelAssignee}</label>
          <select
            id="assignee"
            value={form.assigneeId ?? ''}
            onChange={e => setForm(f => ({
              ...f,
              assigneeId: e.target.value === '' ? null : Number(e.target.value),
            }))}
          >
            <option value="">{i18n.optionUnassigned}</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>{m.username}</option>
            ))}
          </select>
        </div>

        {/* Priority + Due date */}
        <div className="section">
          <label className="field-label" htmlFor="priority">{i18n.labelPriority}</label>
          <select
            id="priority"
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value as Priority | '' }))}
          >
            <option value="">{i18n.optionPriorityDefault}</option>
            {(Object.entries(i18n.priorities) as [Priority, string][]).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>

          <div className="divider" />

          <label className="field-label" htmlFor="due-date">{i18n.labelDueDate}</label>
          <input
            id="due-date"
            type="date"
            value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
          />
        </div>

        {/* Description */}
        <div className="section">
          <label className="field-label" htmlFor="description">{i18n.labelDescription}</label>
          <textarea
            id="description"
            placeholder={i18n.placeholderDescription}
            value={form.description}
            maxLength={8000}
            rows={3}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>

        {/* Subtasks */}
        <div className="section">
          <span className="field-label">{i18n.labelSubtasks}</span>
          {form.subtasks.map((sub, idx) => (
            <div key={idx} className="subtask-row">
              <input
                type="text"
                placeholder={i18n.placeholderSubtask}
                value={sub}
                maxLength={500}
                onChange={e => updateSubtask(idx, e.target.value)}
              />
              <button
                className="btn-icon"
                aria-label={i18n.removeSubtask}
                onClick={() => removeSubtask(idx)}
              >
                ×
              </button>
            </div>
          ))}
          <button className="btn-add-subtask" onClick={addSubtask}>
            {i18n.addSubtask}
          </button>
        </div>

        {submitError && (
          <p className="error-text">{submitError}</p>
        )}

      </div>
    </div>
  );
}
